import { Component, inject, signal, OnInit } from '@angular/core';

import { LucideIconComponent } from '../../../shared/ui/lucide-icon/lucide-icon.component';
import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { TextComponent } from '../../../shared/ui/typography/text/text.component';
import { UserService } from '../../../core/services/user.service';
import { ModalService } from '../../../core/services/modal.service';
import { ToastService } from '../../../shared/ui/toast/toast.service';
import { AuthService } from '../../../core/services/auth.service';
import { formatUserFacingError } from '../../../core/utils/firebase-error.util';
import {
  INVITABLE_ROLES,
  ROLE_LABELS,
  UserRole,
} from '../../../core/models/user.model';

@Component({
  selector: 'app-edit-roles-modal',
  standalone: true,
  imports: [
    LucideIconComponent,
    ButtonComponent,
    TextComponent
],
  template: `
    <div class="flex flex-col">
      <div class="p-6 flex flex-col gap-4">
        @if (userName()) {
        <app-text variant="muted" size="sm">
          Change roles for <span class="font-medium text-foreground">{{ userName() }}</span>
        </app-text>
        }

        <div class="grid gap-2">
          @for (role of invitableRoles; track role) {
          <button
            class="flex items-center gap-3 p-3 rounded-xl border text-left transition-all cursor-pointer"
            [class]="isRoleSelected(role)
              ? 'border-primary bg-primary/5 ring-1 ring-primary'
              : 'border-border bg-surface hover:bg-card'"
            (click)="toggleRole(role)"
          >
            <div class="w-5 h-5 rounded border flex items-center justify-center shrink-0"
                 [class]="isRoleSelected(role)
                   ? 'bg-primary border-primary'
                   : 'border-border'">
              @if (isRoleSelected(role)) {
              <lucide-icon name="Check" class="w-3 h-3 text-primary-foreground"></lucide-icon>
              }
            </div>
            <app-text size="sm" weight="medium">{{ roleLabels[role] }}</app-text>
          </button>
          }
        </div>

        @if (errorMessage()) {
        <div class="p-3 rounded-lg bg-danger/10 border border-danger/20">
          <app-text size="sm" color="danger">{{ errorMessage() }}</app-text>
        </div>
        }
      </div>

      <!-- Footer -->
      <div class="flex flex-col-reverse sm:flex-row justify-end gap-3 p-4 border-t border-border bg-card">
        <app-button variant="secondary" (click)="cancel()" [disabled]="isSaving()">
          Cancel
        </app-button>
        <app-button
          variant="primary"
          (click)="save()"
          [disabled]="selectedRoles().length === 0 || isSaving()"
          [loading]="isSaving()"
        >
          {{ isSaving() ? 'Saving...' : 'Save Roles' }}
        </app-button>
      </div>
    </div>
  `,
})
export class EditRolesModalComponent implements OnInit {
  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthService);
  private readonly modalService = inject(ModalService);
  private readonly toastService = inject(ToastService);

  private get userId(): string | null {
    return this.modalService.state()?.data?.userId || null;
  }

  readonly selectedRoles = signal<UserRole[]>([]);
  readonly userName = signal('');
  readonly isSaving = signal(false);
  readonly errorMessage = signal<string | null>(null);

  readonly invitableRoles = INVITABLE_ROLES;
  readonly roleLabels = ROLE_LABELS;

  ngOnInit() {
    if (!this.userId) return;

    const user = this.userService.getUserById(this.userId);
    if (user) {
      this.userName.set(user.fullName || 'Unknown');
      // Only show invitable roles (filter out driver, safety, etc.)
      const currentRoles = user.currentCompanyRoles.filter((r) =>
        INVITABLE_ROLES.includes(r)
      );
      this.selectedRoles.set([...currentRoles]);
    }
  }

  isRoleSelected(role: UserRole): boolean {
    return this.selectedRoles().includes(role);
  }

  toggleRole(role: UserRole): void {
    const current = this.selectedRoles();
    if (current.includes(role)) {
      this.selectedRoles.set(current.filter((r) => r !== role));
    } else {
      this.selectedRoles.set([...current, role]);
    }
  }

  async save(): Promise<void> {
    if (!this.userId || this.selectedRoles().length === 0) return;

    this.isSaving.set(true);
    this.errorMessage.set(null);

    try {
      // Keep roles that aren't editable here (driver, safety, ...) — only the
      // invitable web roles are replaced.
      const existing = this.userService.getUserById(this.userId)?.currentCompanyRoles || [];
      const preserved = existing.filter((r) => !INVITABLE_ROLES.includes(r));
      await this.userService.addUserToCompany(
        this.userId,
        Array.from(new Set([...preserved, ...this.selectedRoles()]))
      );
      this.toastService.success('Roles updated successfully.');
      this.modalService.close();
    } catch (err: any) {
      this.errorMessage.set(formatUserFacingError(err, 'Failed to update roles.'));
      this.isSaving.set(false);
    }
  }

  cancel(): void {
    this.modalService.close();
  }
}
